import { GeoAutocomplete, GeoAutocompleteAddress } from '@brenger/api-client';
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../../hooks/useDebounce';
import { CacheKey } from '../../../types';
import { routePlannerClient, routePlannerClientToken } from '../../../utils/request';

export interface UseAutoComplete {
  outcome: GeoAutocompleteAddress | null;
  query: string;
  setQuery: (q: string | null) => void;
  setPlaceId: (placeId: string) => void;
  noResults: boolean;
  results: GeoAutocomplete[];
  isLoading: boolean;
  reset: () => void;
  setNewAddress: (a: GeoAutocompleteAddress | null) => void;
}

interface UseAutoCompleteProps {
  currentAddress: GeoAutocompleteAddress | null;
  setNewAddress: (a: GeoAutocompleteAddress | null) => void;
}

export const useAutoComplete = ({ currentAddress, setNewAddress }: UseAutoCompleteProps): UseAutoComplete => {
  const [query, setQuery] = React.useState('');

  const debouncedQuery = useDebounce(query, 500);
  const debouncedQueryIsValid = !!debouncedQuery && debouncedQuery.length > 2;
  const geoResults = useQuery({
    queryKey: [CacheKey.RETRIEVE_GEO_LOCATION, debouncedQuery],
    queryFn: () =>
      routePlannerClient.geo.retrieveAutocomplete({
        query: debouncedQuery,
        sessionToken: routePlannerClientToken,
      }),
    enabled: !!query && debouncedQueryIsValid,
  });
  const results = debouncedQuery ? geoResults.data || [] : [];

  const updatePlace = useMutation({
    mutationFn: routePlannerClient.geo.retrieveLocationDetails,
    onSuccess: data => {
      setQuery('');
      setNewAddress(data.address);
    },
  });

  const updateQuery = (q: string | null): void => {
    updatePlace.reset();
    if (!q) setNewAddress(null);
    setQuery(q || '');
  };

  const updatePlaceId = (pId: string): void => {
    updatePlace.reset();
    updatePlace.mutate({
      placeId: pId,
      sessionToken: routePlannerClientToken,
      includeHouseNumber: true,
      query: debouncedQuery,
    });
  };

  const reset = (): void => {
    setNewAddress(null);
    setQuery('');
  };
  const isLoading = geoResults.isLoading || updatePlace.isPending;

  return {
    outcome: currentAddress,
    query: query || getOutcomeAsText(currentAddress),
    setQuery: updateQuery,
    results: query ? results : [],
    setPlaceId: updatePlaceId,
    setNewAddress,
    isLoading,
    reset,
    noResults: results.length === 0 && debouncedQueryIsValid && !isLoading,
  };
};

const getOutcomeAsText = (outcome: GeoAutocompleteAddress | null): string => {
  const address = [outcome?.line1, outcome?.line2].filter(Boolean).join(' ').trim();
  return [address, outcome?.postal_code, outcome?.locality].filter(Boolean).join(', ');
};
